import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtTokenActions } from '@elio/emanager-front/auth/action';
import { RefreshTokenResponse } from '@elio/emanager-front/auth/model';
import { toElioError } from '@elio/shared/models';
import { LoggerService } from '@elio/shared/tools/logger';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  concatMap,
  first,
  mergeMap,
  of,
  switchMap,
  timeout,
} from 'rxjs';
import { AuthenticationService } from '../data-access/authentication.service';
import { JwtTokenUtils } from './jwt-token.utils';

@Injectable({ providedIn: 'root' })
export class JwtTokenService {
  private TIMEOUT = 5000;
  private newJwtToken$ = new BehaviorSubject<string | null>(null);
  private isRefreshInProgress$ = new BehaviorSubject(false);

  constructor(
    private authService: AuthenticationService,
    private store: Store,
    private loggerService: LoggerService
  ) {}

  retryWithRefreshJwtToken(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.isRefreshInProgress$.pipe(
      first(),
      mergeMap((inProgress) => {
        return !inProgress ? this.refreshCurrentJwtAccessToken() : of(true);
      }),
      mergeMap(() => {
        return this.retryWithNewToken(request, next);
      })
    );
  }

  private refreshCurrentJwtAccessToken(): Observable<boolean> {
    this.newJwtToken$.next(null);
    this.isRefreshInProgress$.next(true);
    return this.authService.refreshToken().pipe(
      switchMap((response: RefreshTokenResponse) => {
        const accessTokenPayload = JwtTokenUtils.extractAccessTokenPayload(response.jwtAccessToken);
        this.store.dispatch(
          JwtTokenActions.refreshJwtTokenSuccess({
            jwtAccessToken: response.jwtAccessToken,
            jwtAccessTokenPayload: accessTokenPayload,
          })
        );
        this.newJwtToken$.next(response.jwtAccessToken);
        this.isRefreshInProgress$.next(false);
        return of(true);
      }),
      catchError((error: unknown) => {
        this.loggerService.warn('refresh JWT failed', error);
        this.isRefreshInProgress$.next(false);
        this.newJwtToken$.next(null);
        this.store.dispatch(JwtTokenActions.refreshJwtTokenFailure({ error: toElioError(error) }));
        return of(false);
      })
    );
  }

  private retryWithNewToken(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.newJwtToken$.pipe(
      timeout(this.TIMEOUT),
      first((token) => token !== null),
      concatMap(
        (token) => next.handle(JwtTokenUtils.addJwtAuthorizationHeader(request, token)) // repeat failed request with new token
      ),
      catchError((err: unknown) => {
        this.loggerService.warn('JWT token not ready yet, cancelling request', err);
        return EMPTY;
      })
    );
  }
}
